/**
 * Captures and reads signup-time URL params across the
 * /register -> /accounts/activate -> /choose-plan hops.
 *
 * Storage: localStorage with a 24-hour TTL. Activation links commonly open
 * in a new tab, where sessionStorage is unavailable. The TTL and explicit
 * clearing prevent old campaign intent from leaking into later visits.
 *
 * Whitelist: only tier/billing/src are persisted here. UTMs are
 * captured by GA4 passively via public/index.html and don't need to
 * round-trip through our backend.
 *
 * Scale + (`scale_plus`) is annual-only. The express CTA in ChoosePlan
 * coerces billing to "annual" when intent.tier === "scale_plus" before
 * displaying the card; the server hard-rejects `scale_plus + monthly`
 * as defense in depth.
 */

const KEYS = {
  TIER: "bi.signup.tier",
  BILLING: "bi.signup.billing",
  SRC: "bi.signup.src",
  CAPTURED_AT: "bi.signup.capturedAt",
}

const INTENT_TTL_MS = 24 * 60 * 60 * 1000
const VALID_TIERS = new Set(["build", "scale", "scale_plus"])
const VALID_BILLING = new Set(["monthly", "annual"])
// Same alphabet the server enforces. Anything else is silently dropped.
const SRC_PATTERN = /^[a-z0-9_-]{1,64}$/

function hasStorage() {
  try {
    return (
      typeof window !== "undefined" &&
      typeof window.localStorage !== "undefined"
    )
  } catch (_e) {
    return false
  }
}

export function captureFromUrl(searchParams) {
  if (!hasStorage()) return
  clearIntent()

  const tier = searchParams.get("tier")
  const billing = searchParams.get("billing")
  const src = searchParams.get("src")
  if (tier && VALID_TIERS.has(tier)) {
    window.localStorage.setItem(KEYS.TIER, tier)
  }
  if (billing && VALID_BILLING.has(billing)) {
    window.localStorage.setItem(KEYS.BILLING, billing)
  }
  if (src && SRC_PATTERN.test(src)) {
    window.localStorage.setItem(KEYS.SRC, src)
  }

  if (tier || billing || src) {
    window.localStorage.setItem(KEYS.CAPTURED_AT, String(Date.now()))
  }
}

export function readIntent() {
  const emptyIntent = { tier: null, billing: null, src: null }
  if (!hasStorage()) return emptyIntent

  const capturedAt = Number(window.localStorage.getItem(KEYS.CAPTURED_AT))
  const age = Date.now() - capturedAt
  if (
    !Number.isFinite(capturedAt) ||
    capturedAt <= 0 ||
    age < 0 ||
    age > INTENT_TTL_MS
  ) {
    clearIntent()
    return emptyIntent
  }

  return {
    tier: window.localStorage.getItem(KEYS.TIER) || null,
    billing: window.localStorage.getItem(KEYS.BILLING) || null,
    src: window.localStorage.getItem(KEYS.SRC) || null,
  }
}

export function clearIntent() {
  if (!hasStorage()) return
  Object.values(KEYS).forEach((k) => window.localStorage.removeItem(k))
}

/** Human-readable tier name. Keep in sync with PLAN_TIERS in limits.js. */
export const TIER_LABELS = {
  build: "Build",
  scale: "Scale",
  scale_plus: "Scale +",
}

/** Display strings for the express CTA. Source of truth: Stripe dashboard. */
export const PRICE_DISPLAY = {
  build: { monthly: "$9/month", annual: "$75/year" },
  scale: { monthly: "$65/month", annual: "$500/year" },
  // Scale + is annual-only; there is no `monthly` row here intentionally.
  scale_plus: { annual: "$900/year" },
}

/**
 * Returns an "effective" billing cadence for a tier+billing pair, after
 * applying the Scale + annual-only coercion. Returns null if the pair
 * is unworkable even after coercion.
 */
export function coerceBilling(tier, billing) {
  if (tier === "scale_plus") return "annual"
  if (VALID_BILLING.has(billing)) return billing
  return null
}

/**
 * True when the intent contains enough info to render an express CTA.
 * The caller is expected to apply `coerceBilling` to the billing field.
 */
export function isExpressEligible(intent) {
  if (!intent || !intent.tier) return false
  if (!TIER_LABELS[intent.tier]) return false
  const billing = coerceBilling(intent.tier, intent.billing)
  if (!billing) return false
  return Boolean(PRICE_DISPLAY[intent.tier]?.[billing])
}
